import React, { useState, useEffect, useRef } from 'react';
import { Modal, List, Typography, Divider, Spin, Input, Button, Tag, Space, Collapse, Table, Card } from 'antd';
import dayjs from 'dayjs';
import { BPMService } from '../api/services/bpm.service';
import { FilesService } from '../api/services/files.service';
import api from '../api/client';
import { YMaps, Map, Polygon, Circle, Placemark } from '@pbe/react-yandex-maps';
import html2canvas from 'html2canvas';
import { Link } from 'react-router-dom';

import { useAuth } from '../store/auth';

const { Text } = Typography;

export default function ProcessDetailModal({ process, open, onCancel, users = [], onRestartProcess }) {
  const { user } = useAuth();
  const [steps, setSteps] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  
  // Rosreestr state
  const [rosreestrData, setRosreestrData] = useState(null);
  const [fetchingRosreestr, setFetchingRosreestr] = useState(false);
  const [rosreestrError, setRosreestrError] = useState('');
  
  // Map state
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapData, setMapData] = useState(null);
  const [fetchingMap, setFetchingMap] = useState(false);
  const [mapError, setMapError] = useState('');
  const [capturingMap, setCapturingMap] = useState(false);
  const mapRef = useRef(null);

  useEffect(() => {
    if (open && process) {
      setRosreestrData(null);
      setRosreestrError('');
      setMapData(null);
      setMapError('');
      fetchDetails();
    }
  }, [open, process]);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const stepsRes = await BPMService.getInstanceSteps(process.id);
      setSteps(stepsRes.data);
      
      const commentsRes = await BPMService.getInstanceComments(process.id);
      setComments(commentsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    const textToSubmit = newComment;
    setNewComment('');
    
    try {
      await BPMService.addInstanceComment(process.id, textToSubmit);
      fetchDetails();
    } catch (err) {
      setNewComment(textToSubmit);
      console.error(err);
    }
  };

  const handleFetchRosreestr = async () => {
    setFetchingRosreestr(true);
    setRosreestrError('');
    try {
      const res = await BPMService.getRosreestrData(process.id);
      if (res && res.data && Array.isArray(res.data.data)) {
        setRosreestrData(res.data.data);
      } else {
        setRosreestrData([]);
        setRosreestrError('Получен неверный формат данных от сервера');
      }
    } catch (err) {
      setRosreestrError(err.response?.data?.detail || 'Ошибка при получении данных');
    } finally {
      setFetchingRosreestr(false);
    }
  };

  const handleFetchMap = async () => {
    setFetchingMap(true);
    setMapError('');
    try {
      const res = await BPMService.getMapData(process.id);
      setMapData(res.data);
      setMapModalOpen(true);
    } catch (err) {
      setMapError(err.response?.data?.detail || 'Ошибка при загрузке карты');
    } finally {
      setFetchingMap(false);
    }
  };

  const handleCaptureMap = async () => {
    setCapturingMap(true);
    try {
      await BPMService.captureMap(process.id);
      fetchDetails();
      setMapModalOpen(false);
      message.success('Ситуационный план успешно прикреплен!');
    } catch (err) {
      console.error(err);
      setMapError('Ошибка при сохранении карты: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCapturingMap(false);
    }
  };

  const handleRestartProcess = async () => {
    try {
      setLoading(true);
      await api.post(`/bpm/instance/${process.id}/restart`);
      message.success('Процесс успешно перезапущен');
      if (onRestartProcess) onRestartProcess();
    } catch (err) {
      message.error(err.response?.data?.detail || 'Ошибка при перезапуске');
    } finally {
      setLoading(false);
    }
  };

  const processStatusMap = {
    'active': 'В процессе',
    'completed': 'Завершено',
    'rejected': 'Отклонено',
    'cancelled': 'Отменено',
    'needs_revision': 'Требует доработки',
    'error': 'Ошибка'
  };

  const stepStatusMap = {
    'pending': 'Ожидает решения',
    'approved': 'Согласовано',
    'rejected': 'Отклонено',
    'skipped': 'Пропущено'
  };

  const statusColors = { pending: 'orange', approved: 'success', rejected: 'error', skipped: 'default' };

  return (
    <Modal
      title={`Детали процесса: ${process?.title}`}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={800}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}><Spin /></div>
      ) : (
        <>
          <div style={{ marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div><Text strong>Статус:</Text> <Tag color={process?.status === 'active' ? 'processing' : process?.status === 'completed' ? 'success' : process?.status === 'needs_revision' ? 'warning' : 'error'}>{processStatusMap[process?.status] || process?.status}</Tag></div>
            <div><Text strong>Файл:</Text> <a href={`/api/v1/files/${process?.target_file_id}/nc-url`} target="_blank" rel="noopener noreferrer">{process?.target_file_name}</a></div>
            <div><Text strong>Раунд:</Text> {process?.round_number}</div>
            <div><Text strong>Запущен:</Text> {dayjs(process?.created_at).format('DD.MM.YYYY HH:mm')}</div>
            {process?.task_id && <div><Text strong>Задача:</Text> <Link to={`/tasks?taskId=${process.task_id}`} onClick={onCancel}>ID {process.task_id}</Link></div>}
            {process?.project_id && <div><Text strong>Проект:</Text> <Link to={`/projects?projectId=${process.project_id}`} onClick={onCancel}>ID {process.project_id}</Link></div>}
          </div>
          
          {process?.status === 'needs_revision' && (process?.editor_id === user?.id || user?.role_level >= 5) && (
              <div style={{ marginBottom: 24 }}>
                  <Button type="primary" danger onClick={handleRestartProcess}>Повторно отправить на согласование (Запустить новый круг)</Button>
              </div>
          )}
          
          {process?.title?.startsWith('Анализ ЗУ') && (
            <Collapse style={{ marginBottom: 24 }}>
              <Collapse.Panel header="Данные из Росреестра" key="1">
                {!rosreestrData ? (
                  <Space direction="vertical">
                    <Button type="primary" onClick={handleFetchRosreestr} loading={fetchingRosreestr}>
                      Получить данные
                    </Button>
                    {rosreestrError && <Text type="danger">{String(rosreestrError)}</Text>}
                  </Space>
                ) : (
                  <div>
                    {Array.isArray(rosreestrData) && rosreestrData.length > 1 && (
                      <div style={{ marginBottom: 16 }}>
                        <Text strong>Общая информация по всем ЗУ:</Text>
                        <ul>
                          <li>Всего участков: {rosreestrData.length}</li>
                          <li>Успешно найдено: {rosreestrData.filter(d => !d.error).length}</li>
                          <li>Общая площадь: {rosreestrData.reduce((acc, curr) => acc + (parseFloat(curr.area) || 0), 0).toFixed(2).replace(/\.00$/, '')} кв.м</li>
                          <li>Общая кадастровая стоимость: {rosreestrData.reduce((acc, curr) => acc + (parseFloat(String(curr.cadastral_cost || '').replace(/[^\d.-]/g, '')) || 0), 0).toLocaleString('ru-RU')} руб.</li>
                        </ul>
                      </div>
                    )}
                    {Array.isArray(rosreestrData) && rosreestrData.map((item, idx) => (
                      <Card size="small" key={idx} title={`Участок: ${item.cadastral_number || 'Неизвестно'}`} style={{ marginBottom: 8 }}>
                        {item.error ? (
                          <Text type="danger">{item.error}</Text>
                        ) : (
                          <ul>
                            <li><b>Адрес:</b> {item.address}</li>
                            <li><b>Площадь:</b> {item.area} кв.м</li>
                            <li><b>Категория:</b> {item.category}</li>
                            <li><b>Использование (по документу):</b> {item.util_by_doc}</li>
                            <li><b>Кадастровая стоимость:</b> {item.cadastral_cost}</li>
                          </ul>
                        )}
                      </Card>
                    ))}
                    {rosreestrError && <Text type="danger" style={{marginTop: 8, display: 'block'}}>{String(rosreestrError)}</Text>}
                  </div>
                )}
              </Collapse.Panel>
              <Collapse.Panel header="Ситуационный план" key="2">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button type="primary" onClick={handleFetchMap} loading={fetchingMap}>
                    Открыть интерактивную карту
                  </Button>
                  {mapError && <Text type="danger">{String(mapError)}</Text>}
                </Space>
              </Collapse.Panel>
            </Collapse>
          )}

          <Modal
            title="Интерактивная карта (Ситуационный план)"
            open={mapModalOpen}
            onCancel={() => setMapModalOpen(false)}
            footer={null}
            width={1000}
            bodyStyle={{ padding: 0 }}
            destroyOnClose
          >
            {mapData && (
              <div style={{ position: 'relative' }}>
                <div ref={mapRef} style={{ display: 'flex', flexDirection: 'row', width: '100%', height: '600px', backgroundColor: '#fff' }}>
                  {/* Legend Sidebar */}
                  <div style={{ width: '250px', overflowY: 'auto', padding: '16px', borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <Typography.Title level={5}>Легенда</Typography.Title>
                    
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ width: 16, height: 16, backgroundColor: '#1e98ff', borderRadius: '50%', marginRight: 8 }} />
                        <Text strong>Транспорт ({mapData.pois.filter(p => p.group === 'transport').length})</Text>
                      </div>
                      {Array.from(new Set(mapData.pois.filter(p => p.group === 'transport').map(p => p.category))).map(cat => {
                        let icon = '🚌';
                        if (cat === 'Станция') icon = '🚆';
                        if (cat === 'Развязка') icon = '🛣️';
                        return <div key={cat} style={{ fontSize: 12, marginLeft: 24, color: '#555' }}>{icon} {cat}</div>;
                      })}
                    </div>
                    
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ width: 16, height: 16, backgroundColor: '#ed4543', borderRadius: '50%', marginRight: 8 }} />
                        <Text strong>Социальная ({mapData.pois.filter(p => p.group === 'social').length})</Text>
                      </div>
                      {Array.from(new Set(mapData.pois.filter(p => p.group === 'social').map(p => p.category))).map(cat => {
                        let icon = '🏫';
                        if (cat === 'Детский сад') icon = '🧸';
                        if (cat === 'Больница/Поликлиника') icon = '🏥';
                        if (cat === 'ВУЗ') icon = '🎓';
                        return <div key={cat} style={{ fontSize: 12, marginLeft: 24, color: '#555' }}>{icon} {cat}</div>;
                      })}
                    </div>

                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ width: 16, height: 16, backgroundColor: '#ffb300', borderRadius: '50%', marginRight: 8 }} />
                        <Text strong>Торговля ({mapData.pois.filter(p => p.group === 'trade').length})</Text>
                      </div>
                      {Array.from(new Set(mapData.pois.filter(p => p.group === 'trade').map(p => p.category))).map(cat => {
                        let icon = '🛒';
                        if (cat === 'ТЦ') icon = '🛍️';
                        if (cat === 'Кафе') icon = '☕';
                        if (cat === 'Ресторан') icon = '🍽️';
                        if (cat === 'Бытовые услуги') icon = '✂️';
                        return <div key={cat} style={{ fontSize: 12, marginLeft: 24, color: '#555' }}>{icon} {cat}</div>;
                      })}
                    </div>

                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ width: 16, height: 16, backgroundColor: '#56db40', borderRadius: '50%', marginRight: 8 }} />
                        <Text strong>Экология ({mapData.pois.filter(p => p.group === 'ecology').length})</Text>
                      </div>
                      {Array.from(new Set(mapData.pois.filter(p => p.group === 'ecology').map(p => p.category))).map(cat => {
                        let icon = '🌳';
                        if (cat === 'Водоем') icon = '💧';
                        return <div key={cat} style={{ fontSize: 12, marginLeft: 24, color: '#555' }}>{icon} {cat}</div>;
                      })}
                    </div>

                    <div style={{ marginTop: 'auto' }}>
                       <Text type="secondary" style={{ fontSize: 11 }}>Контуры: Красный</Text><br/>
                       <Text type="secondary" style={{ fontSize: 11 }}>Радиусы: 300м, 500м, 800м, 1000м, 1500м</Text>
                    </div>
                  </div>

                  {/* Map */}
                  <div style={{ flex: 1, position: 'relative' }}>
                    <YMaps query={{ apikey: 'c83281d1-eede-4c1f-abda-d5c2fb8b41ca' }}>
                      <Map defaultState={{ center: [mapData.center.lat, mapData.center.lon], zoom: 14 }} width="100%" height="100%">
                        
                        {/* Draw Polygons */}
                        {mapData.polygons.map((poly, idx) => {
                          const positions = poly.map(pt => [pt[1], pt[0]]); // Yandex uses [lat, lon]
                          return (
                            <Polygon 
                              key={idx} 
                              geometry={[positions]} 
                              options={{ fillColor: '#FF000033', strokeColor: '#FF0000', strokeWidth: 2 }} 
                            />
                          );
                        })}

                        {/* Draw Circles */}
                        <Circle geometry={[[mapData.center.lat, mapData.center.lon], 1500]} options={{ fill: false, strokeColor: '#FF00FF', strokeStyle: 'shortdash', strokeWidth: 2 }} />
                        <Circle geometry={[[mapData.center.lat, mapData.center.lon], 1000]} options={{ fill: false, strokeColor: '#0000FF', strokeStyle: 'shortdash', strokeWidth: 2 }} />
                        <Circle geometry={[[mapData.center.lat, mapData.center.lon], 800]} options={{ fill: false, strokeColor: '#800080', strokeStyle: 'shortdash', strokeWidth: 2 }} />
                        <Circle geometry={[[mapData.center.lat, mapData.center.lon], 500]} options={{ fill: false, strokeColor: '#FFA500', strokeStyle: 'shortdash', strokeWidth: 2 }} />
                        <Circle geometry={[[mapData.center.lat, mapData.center.lon], 300]} options={{ fill: false, strokeColor: '#008000', strokeStyle: 'shortdash', strokeWidth: 2 }} />

                        {/* Draw Markers */}
                        {mapData.pois.map((poi, idx) => {
                          let preset = 'islands#grayIcon';
                          if (poi.group === 'transport') preset = 'islands#blueIcon';
                          else if (poi.group === 'social') preset = 'islands#redIcon';
                          else if (poi.group === 'trade') preset = 'islands#orangeIcon';
                          else if (poi.group === 'ecology') preset = 'islands#greenIcon';
                          
                          return (
                            <Placemark 
                              key={idx} 
                              geometry={[poi.lat, poi.lon]} 
                              properties={{ balloonContentHeader: `<strong>${poi.name}</strong>`, balloonContentBody: `Категория: ${poi.category}` }}
                              options={{ preset }}
                            />
                          );
                        })}
                      </Map>
                    </YMaps>
                  </div>
                </div>
                <div style={{ padding: '16px', textAlign: 'right', borderTop: '1px solid #f0f0f0' }}>
                  <Button type="primary" onClick={handleCaptureMap} loading={capturingMap}>
                    Сохранить зону карты и прикрепить
                  </Button>
                </div>
              </div>
            )}
          </Modal>
          
          <Divider>Ход согласования</Divider>
          <List
            size="small"
            dataSource={steps}
            renderItem={item => {
              const userObj = users.find(u => String(u.id) === String(item.approver_id));
              return (
              <List.Item>
                <div style={{ width: '100%' }}>
                  <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Text>
                      <b>{userObj ? userObj.full_name : `Сотрудник ID ${item.approver_id}`}</b> (Круг {item.round_number || 1}, Этап {item.stage_number + 1})
                    </Text>
                    <Tag color={statusColors[item.status] || 'default'}>{stepStatusMap[item.status] || item.status}</Tag>
                  </Space>
                  {item.comment && (
                    <div style={{ marginTop: 8, backgroundColor: '#f0f0f0', padding: 8, borderRadius: 4 }}>
                      <Text type="secondary">Комментарий: {item.comment}</Text>
                    </div>
                  )}
                  {item.decided_at && (
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Решение принято: {dayjs(item.decided_at).format('DD.MM.YYYY HH:mm')}
                      </Text>
                    </div>
                  )}
                  {item.delegatee_id && (
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Исполнитель: {users.find(u => String(u.id) === String(item.delegatee_id))?.full_name || `ID ${item.delegatee_id}`}
                      </Text>
                    </div>
                  )}
                </div>
              </List.Item>
            )}}
          />

          <Divider>Обсуждение и история</Divider>
          <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 16 }}>
            <List
              dataSource={comments}
              renderItem={item => {
                const userObj = users.find(u => String(u.id) === String(item.user_id));
                return (
                <List.Item>
                  <List.Item.Meta
                    title={`${userObj ? userObj.full_name : `Пользователь ID ${item.user_id}`} - ${dayjs(item.created_at).format('DD.MM.YYYY HH:mm')}`}
                    description={item.content}
                  />
                </List.Item>
              )}}
            />
          </div>
          
          <div style={{ display: 'flex', gap: 8 }}>
            <Input.TextArea 
              value={newComment} 
              onChange={e => setNewComment(e.target.value)} 
              rows={2} 
              placeholder="Написать комментарий..." 
            />
            <Button type="primary" onClick={handleAddComment} style={{ height: 'auto' }}>
              Отправить
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
